import Navbar from '../home/components/Navbar';
import Footer from '../home/components/Footer';
import { openRandomPlayLink } from '../../config/playLinks';

interface GuideCard {
  category: string;
  date: string;
  readTime: string;
  author: string;
  title: string;
  excerpt: string;
  image: string;
}

const newsGuides: GuideCard[] = [
  {
    category: 'Game Reviews',
    date: 'Mar 15, 2026',
    readTime: '5 min read',
    author: 'Sarah Johnson',
    title: 'Top 10 New Casino Games Released This Month',
    excerpt:
      "Discover the latest and most exciting casino games that have just hit the market. From innovative slots to immersive live dealer experiences, we've tested them all.",
    image: '/game-images/ace333-baccarat.png',
  },
  {
    category: 'Strategy Guide',
    date: 'Feb 12, 2026',
    readTime: '8 min read',
    author: 'Michael Chen',
    title: 'How to Maximize Your Casino Bonuses in 2026',
    excerpt:
      'Learn expert strategies for getting the most value from casino welcome bonuses, free spins, and loyalty programs. Our comprehensive guide covers everything you need to know.',
    image: '/game-images/lanterns-of-luck.png',
  },
  {
    category: 'Industry News',
    date: 'Jan 31, 2026',
    readTime: '6 min read',
    author: 'Emma Rodriguez',
    title: 'Live Casino Trends: What Players Are Choosing Right Now',
    excerpt:
      'From speed baccarat to game show tables, see where player attention is shifting and which live formats are outperforming traditional lobbies this quarter.',
    image: '/game-images/me99-sexy-baccarat.png',
  },
  {
    category: 'Player Safety',
    date: 'Jan 28, 2026',
    readTime: '7 min read',
    author: 'David Kim',
    title: '7 Red Flags to Avoid Before You Deposit',
    excerpt:
      'A practical checklist for spotting risky casino sites before you commit money. We break down payment warning signs, license checks, and hidden bonus traps.',
    image: '/game-images/mrbean9-w-casino.png',
  },
  {
    category: 'Game Reviews',
    date: 'Jan 25, 2026',
    readTime: '4 min read',
    author: 'Liam Cooper',
    title: 'Gates of Olympus vs Sugar Rush: Which One Fits Your Style?',
    excerpt:
      'Two fan-favorite slots, two very different volatility profiles. Compare bonus mechanics, pacing, and practical bankroll expectations before you spin.',
    image: '/game-images/gates-of-olympus.png',
  },
  {
    category: 'Payments',
    date: 'Jan 21, 2026',
    readTime: '6 min read',
    author: 'Hannah Lee',
    title: 'Fastest Withdrawal Methods for Australian Players',
    excerpt:
      'Which payout rails are actually reliable in real play? We compare approval speed, processing windows, and common delays by payment method.',
    image: '/game-images/xe88-roulette24.png',
  },
  {
    category: 'Tips & Tricks',
    date: 'Jan 18, 2026',
    readTime: '5 min read',
    author: 'Noah Wilson',
    title: 'A Smarter Session Plan for High-Volatility Slots',
    excerpt:
      'If you like big-win potential, session structure matters. Use this step-by-step plan to protect your budget while staying in the game longer.',
    image: '/game-images/sugar-rush-1000.png',
  },
  {
    category: 'Guides',
    date: 'Jan 15, 2026',
    readTime: '9 min read',
    author: 'Ava Thompson',
    title: 'RTP, Volatility, Hit Rate: The Metrics That Actually Matter',
    excerpt:
      'Many players misread game stats. This plain-English guide explains how to combine RTP and volatility data to make better game choices.',
    image: '/game-images/rockin-joker.png',
  },
  {
    category: 'Bonuses',
    date: 'Jan 10, 2026',
    readTime: '5 min read',
    author: 'Olivia Turner',
    title: 'No-Deposit Offers: Value Picks or Marketing Trap?',
    excerpt:
      'Some no-deposit offers can be useful, others are nearly impossible to convert. Learn the terms that separate genuinely playable deals from hype.',
    image: '/game-images/caishen-baoxi.png',
  },
  {
    category: 'Industry News',
    date: 'Jan 6, 2026',
    readTime: '7 min read',
    author: 'Ethan Miller',
    title: 'What Casino UX Changes Are Improving Mobile Play in 2026',
    excerpt:
      'From one-tap bet controls to cleaner game lobbies, mobile-first design is changing retention. We highlight the practical UX wins players notice most.',
    image: '/game-images/ipay9-big-gaming.png',
  },
];

export default function NewGuides() {
  const handleOpenPartner = () => openRandomPlayLink();

  return (
    <div className="min-h-screen bg-[#f5f5f7]">
      <Navbar />

      <section className="max-w-7xl mx-auto px-6 py-10">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">News & Guides</h1>
        <p className="text-gray-600 mb-8 max-w-4xl">
          Discover curated casino insights, detailed strategy breakdowns, and reliable industry updates covering market shifts, platform innovations, and evolving player trends across the online casino space.
        </p>

        <div className="space-y-5">
          {newsGuides.map((article) => (
            <article
              key={article.title}
              role="button"
              tabIndex={0}
              onClick={handleOpenPartner}
              onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && handleOpenPartner()}
              className="group bg-white border border-gray-200 rounded-lg hover:shadow-md transition-all duration-300 overflow-hidden cursor-pointer"
            >
              <div className="flex flex-col md:flex-row">
                <div className="relative md:w-[250px] h-48 md:h-auto overflow-hidden flex-shrink-0 bg-gray-200 flex items-center justify-center">
                  <span className="text-gray-500 text-sm font-medium">No Image Available</span>
                  <div className="absolute top-3 left-3 bg-green-600 text-white px-3 py-1 rounded text-xs font-semibold uppercase">
                    {article.category}
                  </div>
                </div>

                <div className="flex-1 p-6">
                  <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-gray-500 mb-3">
                    <span className="flex items-center gap-1">
                      <i className="ri-calendar-line"></i>
                      <span>{article.date}</span>
                    </span>
                    <span className="flex items-center gap-1">
                      <i className="ri-time-line"></i>
                      <span>{article.readTime}</span>
                    </span>
                    <span className="text-gray-400">•</span>
                    <span className="text-gray-700 font-medium">{article.author}</span>
                  </div>

                  <h2 className="text-2xl font-bold text-gray-900 mb-3 group-hover:text-green-700 transition-colors">
                    {article.title}
                  </h2>

                  <p className="text-gray-600 mb-4 leading-relaxed">{article.excerpt}</p>

                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleOpenPartner();
                    }}
                    className="inline-flex items-center text-green-700 font-semibold hover:text-green-800 transition-colors"
                  >
                    Read Full Article
                    <i className="ri-arrow-right-line ml-1"></i>
                  </button>
                </div>
              </div>
            </article>
          ))}
        </div>
      </section>

      <Footer />
    </div>
  );
}
